import { Client, ClientRepresentationStatusEnum } from '@sr-sdks/api-sdk-axios';
import { Avatar, AvatarFallback, Button } from '@sr/ui-library';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { usePaginationBounds } from '../hooks/usePaginationBounds';
import { userInitials } from '../utils/helpers/common';
import { AppPaginationFooter } from './AppPagination';

export const CONFIRM_CLIENTS_PAGE_SIZE = 10;

/**
 * One request, so Confirm All covers every client. Matches the backend's
 * default `PAGINATION_MAX_PAGE_SIZE`; a larger request is a 400.
 */
export const UNCONFIRMED_CLIENTS_FETCH_LIMIT = 1000;

/**
 * Client confirmation list. Since APP-2373 removed the onboarding client steps,
 * its only callers are the Dashboard and MSR3 Home alert modals, which supply
 * their own chrome and their own save step.
 */
export interface IConfirmClientsProps {
  className?: string;
  clientWorkspaceId?: string;
  onUpdatedClients?: (clients: Client[]) => void;
  unconfirmedClients?: Client[];
}

type UnconfirmedClientsSet = {
  clients?: Client[];
};

type ClientRowProps = {
  client: Client;
  onUpdateRepresentationStatus: (
    id: string,
    representationStatus: ClientRepresentationStatusEnum,
  ) => void;
};

/**
 * Decline toggles to Undo once a decision is recorded, so the same button
 * serves both states.
 */
const DecisionButtons = ({
  client: { _id, representationStatus },
  onUpdateRepresentationStatus,
}: ClientRowProps) => {
  const isUnconfirmed =
    representationStatus === ClientRepresentationStatusEnum.Unconfirmed;

  return (
    <>
      <Button
        className="text-destructive hover:bg-destructive/5"
        variant="transparent"
        onClick={() =>
          onUpdateRepresentationStatus(
            _id,
            isUnconfirmed
              ? ClientRepresentationStatusEnum.Declined
              : ClientRepresentationStatusEnum.Unconfirmed,
          )
        }
      >
        {isUnconfirmed ? 'Decline' : 'Undo'}
      </Button>

      {isUnconfirmed && (
        <Button
          onClick={() =>
            onUpdateRepresentationStatus(
              _id,
              ClientRepresentationStatusEnum.Confirmed,
            )
          }
        >
          Confirm
        </Button>
      )}
    </>
  );
};

const ClientRow = ({ client, ...props }: ClientRowProps) => {
  const { clientName, representationStatus } = client;

  return (
    <div className="flex justify-between gap-2 border border-b-0 px-2 py-5 last:border-b hover:bg-sr-light-grey sm:p-3">
      <div className="flex items-center gap-2">
        <Avatar className="font-semibold text-sr-black">
          <AvatarFallback>{userInitials(clientName)}</AvatarFallback>
        </Avatar>
        <div className="break-all text-sm font-normal">{clientName}</div>
      </div>

      <div className="flex flex-col items-center gap-1 sm:flex-row">
        <DecisionButtons client={client} {...props} />

        {representationStatus === ClientRepresentationStatusEnum.Confirmed && (
          <div className="confirmed h-25 relative text-center text-sr-lighter-gray">
            Confirmed
          </div>
        )}

        {representationStatus === ClientRepresentationStatusEnum.Declined && (
          <div className="declined h-25 relative text-center text-sr-lighter-gray">
            Declined
          </div>
        )}
      </div>
    </div>
  );
};

export const ConfirmClients = ({
  className,
  clientWorkspaceId,
  onUpdatedClients,
  unconfirmedClients,
}: IConfirmClientsProps) => {
  const [statusUnconfirmedClients, setStatusUnconfirmedClients] =
    useState<UnconfirmedClientsSet>({ clients: unconfirmedClients });
  const [currentPage, setCurrentPage] = useState(1);

  const numActionedClients =
    statusUnconfirmedClients.clients?.filter(
      (client) =>
        client.representationStatus !==
        ClientRepresentationStatusEnum.Unconfirmed,
    ).length ?? 0;

  /**
   * Whether any client has been answered either way — the one predicate behind
   * the bulk button. It decides the label ("Reset All" vs "Confirm All") and
   * both writes that button fires, so the store and the local list can no
   * longer disagree about what the click meant.
   */
  const hasActionedClient = () => numActionedClients > 0;

  const handleUpdateRepresentationStatus = async (
    id: string,
    representationStatus: ClientRepresentationStatusEnum,
  ) => {
    if (clientWorkspaceId) {
      updateDeclinedConfirmedClients(id, representationStatus);
    }
  };

  const updateDeclinedConfirmedClients = (
    id: string,
    representationStatus: ClientRepresentationStatusEnum,
  ) => {
    const clientsList = statusUnconfirmedClients.clients
      ? statusUnconfirmedClients.clients
      : unconfirmedClients;

    const updatedClientStatus = clientsList?.map((item) => {
      if (item._id === id) {
        return {
          ...item,
          representationStatus,
        };
      }

      return item;
    });

    if (updatedClientStatus) {
      onUpdatedClients?.(updatedClientStatus);
      setStatusUnconfirmedClients({ clients: updatedClientStatus });
    }
  };

  const handleUpdateAllRepresentationStatus = async () => {
    if (clientWorkspaceId) {
      updateAllUnConfirmedClients();
    }
  };

  const updateAllUnConfirmedClients = async () => {
    const status = hasActionedClient()
      ? ClientRepresentationStatusEnum.Unconfirmed
      : ClientRepresentationStatusEnum.Confirmed;

    const updatedClientStatus = statusUnconfirmedClients.clients?.map(
      (item) => {
        return {
          ...item,
          representationStatus: status,
        };
      },
    );

    if (updatedClientStatus) {
      onUpdatedClients?.(updatedClientStatus);
      setStatusUnconfirmedClients({ clients: updatedClientStatus });
    }
  };

  useEffect(() => {
    if (!unconfirmedClients) return;

    setStatusUnconfirmedClients({ clients: unconfirmedClients });
  }, [unconfirmedClients]);

  const numClients = statusUnconfirmedClients.clients?.length ?? 0;
  const totalPages = Math.ceil(numClients / CONFIRM_CLIENTS_PAGE_SIZE);
  // Clamped rather than reset: every decision feeds the list back in as a new
  // prop, and resetting on that would jump the user back to page one.
  const { clampedPage: page } = usePaginationBounds({
    page: currentPage,
    setPage: setCurrentPage,
    totalPages,
  });
  const pageClients = statusUnconfirmedClients.clients?.slice(
    (page - 1) * CONFIRM_CLIENTS_PAGE_SIZE,
    page * CONFIRM_CLIENTS_PAGE_SIZE,
  );

  return (
    <div
      className={twMerge('flex h-full w-full flex-col gap-3 pt-3', className)}
    >
      {statusUnconfirmedClients.clients &&
        statusUnconfirmedClients.clients.length > 0 && (
          <Button
            className="ml-auto mr-3 block"
            variant={hasActionedClient() ? 'secondary' : 'default'}
            onClick={handleUpdateAllRepresentationStatus}
          >
            {/*
             * The count names decisions on other pages, which Reset All
             * also clears (APP-2334).
             */}
            {hasActionedClient()
              ? `Reset All (${numActionedClients} decided)`
              : 'Confirm All'}
          </Button>
        )}

      <div className="overflow-auto">
        {pageClients?.map((client) => (
          <ClientRow
            key={client._id}
            client={client}
            onUpdateRepresentationStatus={handleUpdateRepresentationStatus}
          />
        ))}
      </div>

      <AppPaginationFooter
        currentPage={page}
        pageSize={CONFIRM_CLIENTS_PAGE_SIZE}
        totalItems={numClients}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};
